import React, { useState } from 'react'

import { Check } from 'lucide-react'

import sidebarLogoWhite from '@/assets/images/logos/sidebar.logo-white.png'
import { useTranslation } from '@/core/i18n/useTranslation'
import { track } from '@/shared/analytics/analyticsService'

import './register.css'

type MusicPriorityOption = 'remote_control' | 'legal_music' | 'brand_identity'

interface RegisterStep2Props {
	onNextStep: () => void
}

export const RegisterStep2: React.FC<RegisterStep2Props> = ({ onNextStep }) => {
	const { t } = useTranslation('auth')
	const [selectedOption, setSelectedOption] =
		useState<MusicPriorityOption | null>(null)

	const options: { key: MusicPriorityOption; label: string }[] = [
		{
			key: 'remote_control',
			label: t('register.musicPriority.options.remoteControl'),
		},
		{
			key: 'legal_music',
			label: t('register.musicPriority.options.legalMusic'),
		},
		{
			key: 'brand_identity',
			label: t('register.musicPriority.options.brandIdentity'),
		},
	]

	const handleOptionSelect = (optionKey: MusicPriorityOption) => {
		setSelectedOption(optionKey)

		track('onboarding_music_priority_selected', {
			selected_option: optionKey,
			selected_label: options.find((o) => o.key === optionKey)?.label,
		})

		// Wait 1.5 seconds then advance to next step
		setTimeout(() => {
			onNextStep()
		}, 1500)
	}

	return (
		<div className="flex flex-col items-center justify-between h-full min-h-[500px] text-center px-4">
			<div className="flex-1 flex flex-col justify-center w-full max-w-md">
				{/* Title */}
				<h1 className="register-title font-bold mb-8 text-left">
					{t('register.musicPriority.title')}
				</h1>

				{/* Options */}
				<div className="flex flex-col gap-3 w-full">
					{options.map((option) => {
						const isSelected = selectedOption === option.key
						const hasSelection = selectedOption !== null
						const isNotSelected = hasSelection && !isSelected

						return (
							<button
								key={option.key}
								type="button"
								onClick={() =>
									!selectedOption && handleOptionSelect(option.key)
								}
								disabled={hasSelection}
								className={`
									relative flex items-center gap-3 w-full px-4 py-4 rounded-xl text-left text-sm font-medium transition-all duration-300
									${
										isSelected
											? 'bg-white text-black border-2 border-white shadow-[0_0_20px_rgba(116,85,253,0.6)]'
											: isNotSelected
												? 'bg-[#1a1a1a] text-gray-500 border border-transparent'
												: 'bg-[#1a1a1a] text-white border border-transparent hover:bg-[#252525] cursor-pointer'
									}
								`}
							>
								{isSelected && (
									<div className="flex-shrink-0 w-5 h-5 rounded-full bg-black flex items-center justify-center">
										<Check className="w-3 h-3 text-white" />
									</div>
								)}
								<span className={isSelected ? 'flex-1' : ''}>
									{option.label}
								</span>
							</button>
						)
					})}
				</div>
			</div>

			{/* Logo at bottom */}
			<div className="mt-8 mb-4">
				<img
					src={sidebarLogoWhite}
					alt="Brandtrack"
					className="h-10 opacity-60"
				/>
			</div>
		</div>
	)
}
